import { InputLabel } from 'components/2-molecules/Form';
import { FormfieldDate, FormfieldDateProps } from 'components/2-molecules/Form/FormfieldDate/FormfieldDate';
import { FormfieldInput } from 'components/2-molecules/Form/FormfieldInput/FormfieldInput';
import { FormFieldSelectOption, FormfieldSelect } from 'components/2-molecules/Form/FormfieldSelect/FormfieldSelect';
import { DatePickerCustomHeaderProps } from 'components/2-molecules/Inputs/DatePicker/DatePickerCustomHeader/DatePickerCustomHeader';
import differenceInBusinessDays from 'date-fns/differenceInBusinessDays';
import { useEffect, useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { TravelDeductionFormData, TravelDeductionProps, TravelDeductionStepState } from '../TravelDeduction';
import { TravelDeductionField } from '../TravelDeductionField';
import styles from './TravelStep.module.scss';

export interface TravelStepFieldText {
	label: string;
	helpText?: string;
	helpTextTitle?: string;
	placeholder?: string;
}
export interface TravelStepProps {
	yearValues?: FormFieldSelectOption[];
	year: TravelStepFieldText;
	startDate: TravelStepFieldText;
	endDate: TravelStepFieldText;
	distance: TravelStepFieldText;
	averageWorkingDays: TravelStepFieldText;
	workingDaysWithTransport: TravelStepFieldText;
	units: TravelDeductionProps['units'];
	savedData?: TravelDeductionFormData;
	setValue: (name: string, value: string) => void;
	control: FormfieldDateProps['control'];
	register: { (name: string, { required }: TravelDeductionStepState) };
	datePickerAriaLabels: DatePickerCustomHeaderProps['ariaLabels'];
	errors?: FieldErrors<TravelDeductionFormData>;
	errorMessage: string;
}

export const TravelStep = ({
	distance,
	yearValues,
	year,
	startDate,
	endDate,
	averageWorkingDays,
	workingDaysWithTransport,
	units,
	savedData,
	setValue,
	control,
	register,
	datePickerAriaLabels,
	errors,
	errorMessage,
}: TravelStepProps) => {
	const currentYear = savedData?.year ? Number(savedData.year) : 2025;
	const firstYearDate = new Date(currentYear, 0, 1);
	const lastYearDate = new Date(currentYear, 11, 31);
	const yearlyBusinessDays = differenceInBusinessDays(lastYearDate, firstYearDate);
	const [yearlyAverageDaysOffBuffer, setYearlyAverageDaysOffBuffer] = useState(1);
	const yearlyAverageDaysOff = 80 + yearlyAverageDaysOffBuffer;

	const [daysBetweenDates, setDaysBetweenDays] = useState(yearlyBusinessDays);
	const [dateStart, setDateStart] = useState(firstYearDate);
	const [dateEnd, setDateEnd] = useState(lastYearDate);

	useEffect(() => {
		dateStart.getFullYear() === 2024 ? setYearlyAverageDaysOffBuffer(1) : setYearlyAverageDaysOffBuffer(0);
		const differenceInDays = differenceInBusinessDays(dateEnd, dateStart);
		const differenceInPercentage = (differenceInDays / yearlyBusinessDays) * 100;
		const daysOffInPeriod = (differenceInPercentage * yearlyAverageDaysOff) / 100;
		const averageDifference = Math.trunc(differenceInDays - daysOffInPeriod);
		setDaysBetweenDays(averageDifference);
		setValue && setValue('daysBetweenDates', String(averageDifference));
	}, [dateEnd, dateStart, setValue, yearlyAverageDaysOff, yearlyAverageDaysOffBuffer, yearlyBusinessDays]);

	const handleYearChange = (event) => {
		const updatedYear = event.currentTarget.value;
		const firstYearDate = new Date(updatedYear, 0, 1);
		const lastYearDate = new Date(updatedYear, 11, 31);
		setValue('year', updatedYear);
		setValue('dateStart', String(firstYearDate));
		setValue('dateEnd', String(lastYearDate));
		setDateStart(firstYearDate);
		setDateEnd(lastYearDate);
	};
	return (
		<div className={styles.TravelStep}>
			<FormfieldInput
				id="distance"
				name="distance"
				defaultValue={savedData?.distance}
				type="number"
				min={1}
				label={distance.label}
				helpText={distance.helpText}
				helpTextTitle={distance.helpTextTitle}
				placeholder={distance.placeholder}
				decoratorText={units.kilometers}
				register={register}
				state={errors?.distance ? { hasError: true, required: true } : { required: true }}
				errorMessage={errorMessage}
			/>
			<FormfieldSelect
				id={'year'}
				name={'year'}
				options={yearValues}
				defaultValue={savedData.year ? savedData.year : String(currentYear)}
				register={register}
				state={errors?.year ? { hasError: true, required: true } : { required: true }}
				onChange={(c) => handleYearChange(c)}
				{...year}
			/>
			<FormfieldDate
				id="dateStart"
				name="dateStart"
				label={startDate.label}
				ariaLabels={datePickerAriaLabels}
				controlled
				defaultValue={dateStart}
				enableYearSelect={false}
				onDateChange={(v) => {
					setDateStart(v);
				}}
				state={errors?.dateStart ? { hasError: true, required: true } : { required: true }}
				control={control}
				errorMessage={errorMessage}
			/>
			<FormfieldDate
				id="dateEnd"
				name="dateEnd"
				label={endDate.label}
				ariaLabels={datePickerAriaLabels}
				controlled
				defaultValue={dateEnd}
				enableYearSelect={false}
				onDateChange={(v) => {
					setDateEnd(v);
				}}
				state={errors?.dateEnd ? { hasError: true, required: true } : { required: true }}
				control={control}
				errorMessage={errorMessage}
			/>
			<TravelDeductionField>
				<InputLabel
					className={styles.TravelStep_fieldLabel}
					forId="averageWorkingDays"
					text={averageWorkingDays.label}
					helpText={averageWorkingDays.helpText}
					helpTextHeader={averageWorkingDays.helpTextTitle}
				/>
				<p className={styles.TravelStep_fieldValue} id="averageWorkingDays">
					{daysBetweenDates} {units.days}
				</p>
			</TravelDeductionField>

			<FormfieldInput
				id="workingDaysWithTransport"
				name="workingDaysWithTransport"
				defaultValue={savedData?.workingDaysWithTransport}
				type="number"
				min={1}
				label={workingDaysWithTransport.label}
				helpText={workingDaysWithTransport.helpText}
				helpTextTitle={workingDaysWithTransport.helpTextTitle}
				placeholder={workingDaysWithTransport.placeholder}
				decoratorText={units.days}
				register={register}
				state={errors?.workingDaysWithTransport ? { hasError: true, required: true } : { required: true }}
				errorMessage={errorMessage}
			/>
		</div>
	);
};
